import { Loader2 } from "lucide-react";
import type { HTMLAttributes, ReactNode, TableHTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { EmptyState as BaseEmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";

type ContainerProps = HTMLAttributes<HTMLDivElement>;
type TableProps = TableHTMLAttributes<HTMLTableElement>;
type ThProps = ThHTMLAttributes<HTMLTableCellElement>;
type TdProps = TdHTMLAttributes<HTMLTableCellElement>;

type StateRowProps = {
  colSpan: number;
  title: string;
  description?: string;
  className?: string;
};

type LoadingRowProps = {
  colSpan: number;
  message?: string;
  className?: string;
};

function Container({ className, ...props }: ContainerProps) {
  return (
    <div
      className={cn(
        "overflow-x-auto -mx-4 sm:mx-0 rounded-none sm:rounded-[10px] bg-[var(--surface)]",
        className,
      )}
      {...props}
    />
  );
}

function Table({ className, ...props }: TableProps) {
  return (
    <table
      className={cn("min-w-full text-[13px]", className)}
      {...props}
    />
  );
}

function Th({ className, ...props }: ThProps) {
  return (
    <th
      className={cn(
        "border-b border-[var(--border)] bg-[var(--surface-muted)] px-[8px] py-[8px] sm:px-[14px] sm:py-[10px] text-left text-[11.5px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]",
        className,
      )}
      {...props}
    />
  );
}

function Td({ className, ...props }: TdProps) {
  return (
    <td
      className={cn(
        "border-b border-[var(--border-light,#f3f4f6)] px-[8px] py-[9px] sm:px-[14px] sm:py-[11px] text-[var(--foreground)]",
        className,
      )}
      {...props}
    />
  );
}

function EmptyState({ colSpan, title, description, className }: StateRowProps) {
  return (
    <tr>
      <td colSpan={colSpan} className={cn("px-4 py-6", className)}>
        <BaseEmptyState title={title} description={description} />
      </td>
    </tr>
  );
}

function LoadingSkeleton({ colSpan, message = "Carregando...", className }: LoadingRowProps) {
  return (
    <tr>
      <td colSpan={colSpan} className={cn("px-4 py-8 text-center text-[13px] text-[var(--muted-foreground)]", className)}>
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--primary)]" />
          {message}
        </span>
      </td>
    </tr>
  );
}

export const AppTable: {
  Container: typeof Container;
  Table: typeof Table;
  Th: typeof Th;
  Td: typeof Td;
  EmptyState: typeof EmptyState;
  LoadingSkeleton: typeof LoadingSkeleton;
} = {
  Container,
  Table,
  Th,
  Td,
  EmptyState,
  LoadingSkeleton,
};

export type AppTableChildren = ReactNode;
